import {WidgetFullEditorPortal} from "../common/WidgetFullEditorPortal"
import {StickyNoteData} from "@dv5/domain"
import {FC} from "react"
import {useWidgetEditState} from "../../hooks/useWidgetEditState"
import {useWidgetEditor} from "../../hooks/useWidgetEditor"
import {EditorWidgetProps} from "../../model/WidgetProps"
import {widgetService} from "../../services/widgetService"
import {WidgetPlainText} from "../common/WidgetPlainText"
import {getReactionIcon} from "../common/ReactionPicker"
import {WidgetFrame} from "../common/WidgetFrame"
import {InlineEditor} from "./InlineEditor"
import {StickyNoteFullEditor} from "./StickyNoteFullEditor"
import {StickyNoteToolbar} from "./StickyNoteToolbar"
import './StickyNote.less'

type Props = EditorWidgetProps

export const StickyNote: FC<Props> = ({
                                          canvasId,
                                          widget,
                                          selected,
                                          settings,
                                          editingWidgetId,
                                          onStartDrag,
                                          onStartResize,
                                          onContextMenu,
                                          dragState,
                                          onStartLinkCreation,
                                          onCompleteLinkCreation,
                                          onNavigate,
                                          onDeleteDataLink,
                                          onStopEditing,
                                      }) => {
    const data = widget.data as StickyNoteData
    const styles = widgetService.getWidgetStyle(widget)
    const classes = widgetService.getWidgetClasses(widget, 'stickyNote', selected, editingWidgetId, widget.commentsCount > 0)
    const {isFullEditor, setIsFullEditor, stopEditing} = useWidgetEditState(onStopEditing, settings.readOnly)

    const {
        containerRef,
        resizeCursor,
        handlers,
        renderResizeHandles,
        renderLinkDots,
    } = useWidgetEditor({
        widget,
        selected,
        editingWidgetId,
        dragState,
        onStartDrag,
        onStartResize,
        onContextMenu,
        onStartLinkCreation,
        onCompleteLinkCreation,
        onNavigate,
        onDeleteDataLink,
    })

    const renderReaction = () => {
        if (!data.reactionType) return null
        const ReactionIcon = getReactionIcon(data.reactionType)
        return ReactionIcon ? (
            <div className='sticky-note-reaction'>
                <ReactionIcon width="20" height="20" />
            </div>
        ) : null
    }

    if (editingWidgetId && editingWidgetId === widget.id) {
        const toolbarStyles = widgetService.getToolbarStyle(widget)
        return (
            <div className='sticky-note-edit-container' ref={containerRef} onContextMenu={handlers.onContextMenu}>
                {!isFullEditor && (
                    <StickyNoteToolbar
                        canvasId={canvasId}
                        widgetId={widget.id}
                        data={data}
                        toolbarStyles={toolbarStyles}
                        onExpandToFull={() => setIsFullEditor(true)}
                    />
                )}
                <div className={classes} style={styles}>
                    <InlineEditor widgetId={widget.id}
                                  text={data.text}
                                  canvasId={canvasId}
                                  data={data}
                                  containerRef={containerRef}
                                  contentStyle={widgetService.getWidgetContentStyle(widget)}
                    />
                    {renderReaction()}
                </div>
                {isFullEditor && (
                    <WidgetFullEditorPortal>
                        <StickyNoteFullEditor
                            canvasId={canvasId}
                            widgetId={widget.id}
                            data={data}
                            toolbarStyles={toolbarStyles}
                            onClose={stopEditing}
                        />
                    </WidgetFullEditorPortal>
                )}
            </div>
        )
    }

    return (
        <WidgetFrame
            baseClass='stickyNote'
            canvasId={canvasId}
            widget={widget}
            settings={settings}
            selected={selected}
            editingWidgetId={editingWidgetId}
            resizeCursor={resizeCursor}
            handlers={handlers}
            containerRef={containerRef}
            renderResizeHandles={renderResizeHandles}
            renderLinkDots={renderLinkDots}
            onNavigate={onNavigate}
            onDeleteDataLink={onDeleteDataLink}
        >
            <WidgetPlainText text={data.text} style={widgetService.getWidgetContentStyle(widget)} />
            {renderReaction()}
        </WidgetFrame>
    )
}
